import * as SuperAdmin from 'constants/super-admin';
import type { CostCenterInvite } from '@/types/cost-center-invite';

// The super-admin table actions are still dispatched from untyped ducks; each
// reducer reads only the fields it needs off a minimally-typed action boundary.
interface SelectedRowsAction {
  type: string;
  licenseId?: string;
  user?: { id: string } & Record<string, unknown>;
  invites?: CostCenterInvite[];
  costCenter?: Record<string, unknown>;
  invitesMap?: Record<string, CostCenterInvite[]>;
  costCenterId?: string;
}

type SelectedLicenseRowsState = Record<string, boolean>;
type SelectedUserRowsState = Record<string, unknown>;
// The selected cost center's invites, keyed by their position in the fetched list.
type SelectedCostCenterInvitesState = Record<string, CostCenterInvite>;
type SelectedCostCenterDetailsState = Record<string, unknown>;

interface AllCostCenterInvitesState {
  invitesByCostCenter: Record<string, CostCenterInvite[]>;
  fetchFailed: boolean;
}

interface CostCenterInvitesCacheState {
  invitesByCostCenterId: Record<string, CostCenterInvite[]>;
  // Cost centers whose most recent invite fetch failed; cleared for a cost center when a later
  // fetch of it succeeds. Per cost center, so one failure can't read as an error for another.
  fetchFailedByCostCenterId: Record<string, boolean>;
}

const initialState = {};

export const SelectedLicenseRowsReducer = (
  state: SelectedLicenseRowsState = initialState,
  action: SelectedRowsAction,
): SelectedLicenseRowsState => {
  switch (action.type) {
    case SuperAdmin.TABLE_ACTION_TYPES.SELECT_LICENSE_ROW:
      return {
        // reinstate the spread operator below if we want multi-select
        // ...state,
        [action.licenseId as string]: true,
      };
    case SuperAdmin.TABLE_ACTION_TYPES.DESELECT_LICENSE_ROW: {
      const { [action.licenseId as string]: omit, ...rest } = state;
      return rest;
    }
    case SuperAdmin.SELECTOR_ACTION_TYPES.SELECT_TOOL:
      return initialState;
    default:
      return state;
  }
};

export const SelectedUserRowsReducer = (
  state: SelectedUserRowsState = initialState,
  action: SelectedRowsAction,
): SelectedUserRowsState => {
  switch (action.type) {
    case SuperAdmin.TABLE_ACTION_TYPES.SELECT_USER_ROW:
      return {
        ...state,
        [(action.user as { id: string }).id]: action.user,
      };
    case SuperAdmin.TABLE_ACTION_TYPES.DESELECT_USER_ROW: {
      const { [(action.user as { id: string }).id]: omit, ...rest } = state;
      return rest;
    }
    case SuperAdmin.TABLE_ACTION_TYPES.DESELECT_ALL_USER_ROWS:
    case SuperAdmin.SELECTOR_ACTION_TYPES.SELECT_TOOL:
      return initialState;
    default:
      return state;
  }
};

export const SelectedCostCenterInvitesReducer = (
  state: SelectedCostCenterInvitesState = initialState,
  action: SelectedRowsAction,
): SelectedCostCenterInvitesState => {
  const { invites } = action;

  switch (action.type) {
    case SuperAdmin.TABLE_ACTION_TYPES.SET_COSTCENTER_INVITES:
      return Object.fromEntries((invites ?? []).map((invite, index) => [index, invite]));
    case SuperAdmin.TABLE_ACTION_TYPES.DESELECT_LICENSE_ROW:
    case SuperAdmin.SELECTOR_ACTION_TYPES.SELECT_TOOL:
      return initialState;
    default:
      return state;
  }
};

export const SelectedCostCenterDetails = (
  state: SelectedCostCenterDetailsState = initialState,
  action: SelectedRowsAction,
): SelectedCostCenterDetailsState => {
  const { costCenter } = action;
  switch (action.type) {
    case SuperAdmin.TABLE_ACTION_TYPES.SET_COSTCENTER_DETAILS:
      return {
        ...costCenter,
      };
    case SuperAdmin.TABLE_ACTION_TYPES.DESELECT_LICENSE_ROW:
    case SuperAdmin.SELECTOR_ACTION_TYPES.SELECT_TOOL:
      return initialState;
    default:
      return state;
  }
};

const allCostCenterInvitesInitialState: AllCostCenterInvitesState = {
  invitesByCostCenter: {},
  fetchFailed: false,
};

export const AllCostCenterInvitesReducer = (
  state: AllCostCenterInvitesState = allCostCenterInvitesInitialState,
  action: SelectedRowsAction,
): AllCostCenterInvitesState => {
  switch (action.type) {
    case SuperAdmin.TABLE_ACTION_TYPES.SET_ALL_COST_CENTER_INVITES:
      return {
        invitesByCostCenter: action.invitesMap || {},
        fetchFailed: false,
      };
    case SuperAdmin.TABLE_ACTION_TYPES.SET_COST_CENTER_INVITES_FETCH_ERROR:
      return {
        invitesByCostCenter: {},
        fetchFailed: true,
      };
    case SuperAdmin.SELECTOR_ACTION_TYPES.SELECT_TOOL:
      return allCostCenterInvitesInitialState;
    default:
      return state;
  }
};

const costCenterInvitesCacheInitialState: CostCenterInvitesCacheState = {
  invitesByCostCenterId: {},
  fetchFailedByCostCenterId: {},
};

// Caps how many distinct cost centers' invites stay cached at once, so browsing many licenses in
// one SuperAdmin session doesn't grow this map unboundedly - object key order is insertion order
// for these non-numeric UUID keys, so dropping the oldest keeps the most-recently-viewed ones.
const MAX_CACHED_COST_CENTER_INVITES = 25;

// Keyed by costCenterId so invites for more than one cost center can be cached at once (e.g. the
// License Detail modal being reopened for a license in a different cost center within the same
// session), rather than each fetch clobbering the last one. Reuses the existing cost-center-scoped
// ADMIN_GET_COST_CENTER_INVITES backend action (each invite already carries its own productAccessId
// - see UserProductInviteV3), so a consumer that needs counts for one specific license/productAccess
// filters this cost center's invites by productAccessId itself, rather than this cache needing a
// separate productAccessId-scoped fetch and backend action per license.
export const CostCenterInvitesCacheReducer = (
  state: CostCenterInvitesCacheState = costCenterInvitesCacheInitialState,
  action: SelectedRowsAction,
): CostCenterInvitesCacheState => {
  switch (action.type) {
    case SuperAdmin.TABLE_ACTION_TYPES.SET_COST_CENTER_INVITES_CACHE: {
      const costCenterId = action.costCenterId as string;
      // Re-insert rather than overwrite in place so a refreshed cost center counts as the most
      // recently viewed one for the cap below.
      const { [costCenterId]: replaced, ...others } = state.invitesByCostCenterId;
      const merged = { ...others, [costCenterId]: action.invites || [] };
      const keys = Object.keys(merged);
      const invitesByCostCenterId =
        keys.length <= MAX_CACHED_COST_CENTER_INVITES
          ? merged
          : keys
              .slice(keys.length - MAX_CACHED_COST_CENTER_INVITES)
              .reduce<Record<string, CostCenterInvite[]>>((acc, key) => {
                acc[key] = merged[key];
                return acc;
              }, {});
      const { [costCenterId]: cleared, ...stillFailed } = state.fetchFailedByCostCenterId;
      return {
        invitesByCostCenterId,
        fetchFailedByCostCenterId: stillFailed,
      };
    }
    case SuperAdmin.TABLE_ACTION_TYPES.SET_COST_CENTER_INVITES_CACHE_FETCH_ERROR:
      return {
        ...state,
        fetchFailedByCostCenterId: {
          ...state.fetchFailedByCostCenterId,
          [action.costCenterId as string]: true,
        },
      };
    case SuperAdmin.SELECTOR_ACTION_TYPES.SELECT_TOOL:
      return costCenterInvitesCacheInitialState;
    default:
      return state;
  }
};
